import { useEffect, useState } from "react";
import api from "@/lib/api";
import { StageBadge, STAGES, NEXT } from "@/pages/farm/shared";
import { ArrowRight, AlertTriangle, ExternalLink, FileText, Printer, CheckSquare, Square, Download } from "lucide-react";

function batchPrintSlips(slips) {
  const w = window.open("", "_blank", "width=720,height=900");
  const pages = slips.map(slip => {
    const addr = slip.shipping_address || {};
    const items = (slip.items || []).map(i => `<tr><td>${i.name}</td><td style="text-align:right">${i.qty}</td><td style="text-align:right">$${(i.price*i.qty).toFixed(2)}</td></tr>`).join("");
    return `
      <section class="page">
        <h1>The Farm To You</h1>
        <p class="muted">Packing slip · order #${slip.order_id.slice(0,12)}</p>
        <div class="box"><strong>Ship to:</strong><br/>${slip.customer?.name || ""}<br/>${addr.line1 || ""}<br/>${addr.city || ""}, ${addr.state || ""} ${addr.postal_code || ""}<br/>${addr.country || ""}</div>
        <table><thead><tr><th>Item</th><th style="text-align:right">Qty</th><th style="text-align:right">Subtotal</th></tr></thead><tbody>${items}</tbody></table>
        <p class="muted" style="margin-top:18px">Subtotal $${slip.subtotal.toFixed(2)} · Generated ${new Date().toLocaleString()}</p>
      </section>`;
  }).join("");
  w.document.write(`<!DOCTYPE html><html><head><title>Batch packing slips</title>
    <style>body{font-family:Georgia,serif;color:#1a1a1a}h1{font-size:30px;margin:0}.muted{color:#888}table{width:100%;border-collapse:collapse;margin-top:18px}th,td{padding:8px 6px;border-bottom:1px solid #e5e5e5;text-align:left}.box{border:1px solid #ddd;padding:16px;margin-top:18px;border-radius:8px}.page{padding:42px;page-break-after:always}</style>
    </head><body>${pages}<script>window.print()</script></body></html>`);
  w.document.close();
}

export default function OrdersTab({ canFulfill, onOpenOrder, refreshToken }) {
  const [queue, setQueue] = useState(null);
  const [stageFilter, setStageFilter] = useState("paid");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkMsg, setBulkMsg] = useState("");

  const load = () => api.get("/farm/fulfillment/queue").then(r => setQueue(r.data)).catch(e => setError(e.response?.data?.detail || "Load error"));
  useEffect(() => { load(); }, [refreshToken]);

  // Wipe selection when changing stages so we never apply a transition meant for a different bucket.
  useEffect(() => { setSelected(new Set()); setBulkMsg(""); }, [stageFilter]);

  const advance = async (orderId, currentStage) => {
    const next = NEXT[currentStage]; if (!next) return;
    setBusy(true); setError("");
    try {
      await api.post(`/farm/orders/${orderId}/status`, { status: next });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = (orders) => {
    setSelected(prev => prev.size === orders.length ? new Set() : new Set(orders.map(o => o.id)));
  };

  const bulkAdvance = async () => {
    const ids = Array.from(selected); if (!ids.length) return;
    const nextStage = NEXT[stageFilter]; if (!nextStage) return;
    setBusy(true); setBulkMsg(""); setError("");
    try {
      const { data } = await api.post("/farm/orders/bulk-status", { order_ids: ids, status: nextStage });
      setBulkMsg(data.summary);
      setSelected(new Set());
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Bulk update failed");
    } finally { setBusy(false); }
  };

  const bulkPrint = async () => {
    const ids = Array.from(selected); if (!ids.length) return;
    setBusy(true); setError("");
    try {
      const slips = [];
      for (const id of ids) {
        const { data } = await api.get(`/farm/orders/${id}/packing-slip`);
        slips.push(data);
      }
      batchPrintSlips(slips);
    } catch (e) {
      setError(e.response?.data?.detail || "Print failed");
    } finally { setBusy(false); }
  };

  if (!queue) return <div className="p-10 text-[var(--ff-text-muted)]" data-testid="orders-loading">Loading orders…</div>;
  const stageOrders = queue.columns?.[stageFilter] || [];
  const nextStage = NEXT[stageFilter];
  const allSelected = stageOrders.length > 0 && selected.size === stageOrders.length;

  const exportCsv = async () => {
    try {
      const res = await api.get("/farm/sales/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `farm-sales-${today}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="farm-orders-tab">
      <div className="flex flex-wrap gap-2 items-center" data-testid="stage-filters">
        {STAGES.map(s => (
          <button key={s.key} onClick={() => setStageFilter(s.key)} data-testid={`stage-filter-${s.key}`}
            className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${stageFilter === s.key ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white border-[var(--ff-border)] text-[var(--ff-text-secondary)] hover:border-[var(--ff-primary)]"}`}>
            {s.label} · {queue.counts[s.key] || 0}
          </button>
        ))}
        <button onClick={exportCsv} className="ml-auto btn-secondary text-sm inline-flex items-center gap-1.5" data-testid="sales-export-btn">
          <Download size={14}/> Export sales CSV
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800 flex items-center gap-2" data-testid="orders-error">
          <AlertTriangle size={16}/> {error}
        </div>
      )}
      {bulkMsg && <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800" data-testid="bulk-msg">{bulkMsg}</div>}

      {canFulfill && stageOrders.length > 0 && (
        <div className="card-flat p-4 flex items-center flex-wrap gap-3" data-testid="bulk-toolbar">
          <button onClick={() => toggleAll(stageOrders)} className="text-sm inline-flex items-center gap-1.5 text-[var(--ff-text-secondary)] hover:text-[var(--ff-primary)]" data-testid="select-all-toggle">
            {allSelected ? <CheckSquare size={16}/> : <Square size={16}/>}
            {allSelected ? "Clear selection" : `Select all ${stageOrders.length}`}
          </button>
          <span className="text-xs text-[var(--ff-text-muted)]" data-testid="selected-count">{selected.size} selected</span>
          <div className="ml-auto flex gap-2 flex-wrap">
            {selected.size > 0 && (
              <button disabled={busy} onClick={bulkPrint} className="btn-secondary text-sm" data-testid="bulk-print-btn">
                <Printer size={14}/> Print {selected.size} packing slip{selected.size === 1 ? "" : "s"}
              </button>
            )}
            {selected.size > 0 && nextStage && (
              <button disabled={busy} onClick={bulkAdvance} className="btn-primary text-sm" data-testid="bulk-advance-btn">
                Advance {selected.size} to {nextStage} <ArrowRight size={14}/>
              </button>
            )}
          </div>
        </div>
      )}

      {stageOrders.length === 0 && (
        <div className="card-flat p-10 text-center text-[var(--ff-text-muted)]" data-testid="empty-stage">
          No orders in <strong>{stageFilter}</strong> right now.
        </div>
      )}
      <div className="space-y-3">
        {stageOrders.map(o => {
          const stage = o.vendor_status?.status || "paid";
          const next = NEXT[stage];
          const tracking = o.vendor_status?.tracking_url;
          const isSel = selected.has(o.id);
          return (
            <div key={o.id} className={`card-flat p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${isSel ? "ring-2 ring-[var(--ff-primary)]/40" : ""}`} data-testid={`order-row-${o.id}`}>
              <div className="flex-1 min-w-0 flex gap-3">
                {canFulfill && (
                  <button onClick={() => toggle(o.id)} className="mt-1 text-[var(--ff-text-secondary)] hover:text-[var(--ff-primary)]" data-testid={`order-select-${o.id}`} aria-label={isSel ? "Deselect" : "Select"}>
                    {isSel ? <CheckSquare size={18}/> : <Square size={18}/>}
                  </button>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-medium text-sm font-mono text-[var(--ff-text-secondary)]" data-testid={`order-id-${o.id}`}>#{o.id.slice(0,12)}</p>
                    <StageBadge stage={stage}/>
                    <span className="text-xs text-[var(--ff-text-muted)]">{o.created_at?.slice(0,10)}</span>
                  </div>
                  <p className="mt-1 font-display text-lg" data-testid={`order-name-${o.id}`}>{o.items.map(i => `${i.name} ×${i.qty}`).join(", ")}</p>
                  <p className="text-xs text-[var(--ff-text-muted)] mt-1">Subtotal ${o.subtotal.toFixed(2)}</p>
                  {tracking && (
                    <a href={tracking} target="_blank" rel="noreferrer" className="text-xs underline mt-1 inline-flex items-center gap-1" data-testid={`order-tracking-${o.id}`}>
                      Track shipment <ExternalLink size={11}/>
                    </a>
                  )}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => onOpenOrder(o.id)} className="btn-secondary text-sm" data-testid={`order-open-${o.id}`}>
                  <FileText size={14}/> Details
                </button>
                {canFulfill && next && (
                  <button disabled={busy} onClick={() => advance(o.id, stage)} className="btn-primary text-sm" data-testid={`order-advance-${o.id}`}>
                    Mark {next} <ArrowRight size={14}/>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
